import { inject, Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { RtMessage } from '../interfaces/rt-message.interface';
import { SignalRHubService } from './signalR-hub.service';
import { environment } from '../../../environments/environment';



@Injectable({ providedIn: 'root' })
export class RealtimeService {
  private hub?: signalR.HubConnection;

  private readonly connected$ = new BehaviorSubject<boolean>(false);
  private readonly messages$ = new Subject<RtMessage>();
  private readonly signalRHubService= inject(SignalRHubService);

  /** Public streams */
  isConnected$: Observable<boolean> = this.connected$.asObservable(); 

  async start(getAccessToken: () => Promise<string>, hubUrl: string=environment.baseSignalRApiUrl): Promise<void> {
    if (this.hub && this.hub.state !== signalR.HubConnectionState.Disconnected) return;

    this.hub = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: async () => await getAccessToken(), // JWT
        // transport: signalR.HttpTransportType.WebSockets,        // prefer WS
        withCredentials: false
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])   // production-friendly
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // ---- Server -> Client generic handler
    this.hub.on('Receive', (eventName: string, payload: any) => {
        console.log('Received event:', eventName, payload);
        console.log(payload);
      this.messages$.next({ eventName, payload });
      this.signalRHubService.messageReceiver.set({ eventName, payload });
    });

    this.hub.onreconnecting(() => this.connected$.next(false));
    this.hub.onreconnected(() => this.connected$.next(true));
    this.hub.onclose(() => this.connected$.next(false));

    await this.hub.start();
    this.connected$.next(true);
  }

  async stop(): Promise<void> {
    if (!this.hub) return;
    await this.hub.stop();
    this.connected$.next(false);
  }

  /** #5: after a driver switches categories on/off, move this connection to the right new-trip groups. */
  async refreshCategoryGroups(previousCategoryIds: string[]): Promise<void> {
    if (!this.hub || this.hub.state !== signalR.HubConnectionState.Connected) return;
    try {
      await this.hub.invoke('RefreshCategoryGroups', previousCategoryIds);
    } catch {
      // Not critical: the groups are rebuilt on the next reconnect.
    }
  }

  /** Optional: join server-side groups if your hub exposes methods */
  async joinEntityGroup(entityName: string, entityId: string): Promise<void> {
    if (!this.hub) throw new Error('Hub not started');
    await this.hub.invoke('JoinEntityGroup', entityName, entityId);
  }
}